import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, ChevronDown, Camera, Package, Scale, UserCog } from "lucide-react";

const tone = (p) => (p >= 90 ? "bg-green-500" : p >= 70 ? "bg-amber-400" : "bg-rose-500");
const ISSUE_ICON = { "no photo": Camera, "no products": Package, "kilos not balanced": Scale };

function Bar({ r }) {
  const all = r.total + (r.assisted || 0);
  const w = (n) => `${all ? (n / all) * 100 : 0}%`;
  return (
    <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden flex" data-testid={`acc-bar-${r.id}`}>
      <div className={`h-full ${tone(r.pct ?? 0)} transition-[width] duration-500`} style={{ width: w(r.correct) }} />
      <div className="h-full" style={{ width: w(r.total - r.correct) }} />
      {r.assisted > 0 && <div data-testid={`acc-assisted-bar-${r.id}`} title={`${r.assisted} filed by admin`} className="h-full ml-auto bg-[repeating-linear-gradient(45deg,#818cf8_0_4px,#6366f1_4px_8px)]" style={{ width: w(r.assisted) }} />}
    </div>
  );
}

function AssistedList({ r }) {
  if (!r.assisted) return null;
  return (
    <div className="space-y-1.5 pt-1" data-testid={`acc-assisted-${r.id}`}>
      <div className="text-[11px] font-semibold text-indigo-700">Filed by an admin for {r.name} (not counted in the %)</div>
      {r.assisted_logs.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center gap-2 text-xs bg-indigo-50/60 border border-indigo-100 rounded-xl px-3 py-2">
          <span className="font-num text-slate-600">{p.date}</span><span className="font-semibold text-slate-700">{p.line}</span><span className="text-slate-400 capitalize">{p.shift}</span>
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-white border border-indigo-200 text-indigo-700 px-2 py-0.5"><UserCog size={11} /> {p.by}</span>
        </div>
      ))}
    </div>
  );
}

function Row({ r }) {
  const [open, setOpen] = useState(false);
  const bits = [r.no_photo && `${r.no_photo} no photo`, r.no_products && `${r.no_products} no products`, r.unbalanced && `${r.unbalanced} not balanced`].filter(Boolean);
  const help = r.assisted > 0 && <span className="text-indigo-600" data-testid={`acc-assisted-count-${r.id}`}>{r.assisted} filed by admin</span>;
  return (
    <div className="border-t border-slate-100 first:border-0" data-testid={`acc-row-${r.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 py-3 text-left" data-testid={`acc-toggle-${r.id}`}>
        <div className="w-32 sm:w-40 shrink-0"><div className="font-semibold text-slate-800 text-sm truncate">{r.name}</div><div className="text-[11px] text-slate-400">{r.total ? `${r.correct}/${r.total} correct` : "No own logs"}{help && <> · {help}</>}</div></div>
        <Bar r={r} />
        <span className="font-num font-bold text-slate-800 w-12 text-right" data-testid={`acc-pct-${r.id}`}>{r.pct == null ? "—" : `${r.pct}%`}</span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {bits.length > 0 && !open && <div className="text-[11px] text-rose-600 -mt-2 pb-2 pl-0 sm:pl-[10.75rem]">{bits.join(" · ")}</div>}
      {open && (
        <div className="pb-3 space-y-1.5" data-testid={`acc-problems-${r.id}`}>
          {!r.problems.length && r.total > 0 && <div className="text-sm text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={15} /> Every log correct</div>}
          {r.problems.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-2 text-xs bg-rose-50/60 border border-rose-100 rounded-xl px-3 py-2">
              <span className="font-num text-slate-600">{p.date}</span><span className="font-semibold text-slate-700">{p.line}</span><span className="text-slate-400 capitalize">{p.shift}</span>
              <span className="ml-auto flex gap-1.5">{p.issues.map((i) => { const I = ISSUE_ICON[i]; return <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white border border-rose-200 text-rose-700 px-2 py-0.5">{I && <I size={11} />}{i}</span>; })}</span>
            </div>
          ))}
          <AssistedList r={r} />
        </div>
      )}
    </div>
  );
}

export default function AccuracyCard({ week }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (week != null) api.get("/accuracy", { params: { week } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [week]);
  if (!d) return null;
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-4 shadow-sm" data-testid="accuracy-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-heading font-bold text-slate-800">Log accuracy · Week {d.week}</h3>
        {d.overall.pct != null && <span data-testid="acc-overall" className={`font-num font-extrabold text-2xl ${d.overall.pct >= 90 ? "text-green-600" : d.overall.pct >= 70 ? "text-amber-600" : "text-rose-600"}`}>{d.overall.pct}%</span>}
      </div>
      <p className="text-xs text-slate-400 mb-2">A log is correct when it has products (or a reason), kilos that balance, and a photo.{d.overall.total ? ` ${d.overall.correct} of ${d.overall.total} logs correct.` : ""} <span className="inline-flex items-center gap-1 text-indigo-600"><span className="inline-block w-3 h-2 rounded-sm bg-[repeating-linear-gradient(45deg,#818cf8_0_3px,#6366f1_3px_6px)]" /> striped = filed by admin, not counted</span></p>
      {!d.tracked ? <div className="text-sm text-slate-400" data-testid="acc-not-tracked">Accuracy is tracked from Week {d.start_week} onwards.</div>
        : !d.supervisors.length ? <div className="text-sm text-slate-400" data-testid="acc-empty">No logs filed this week yet.</div>
          : d.supervisors.map((r) => <Row key={r.id} r={r} />)}
    </div>
  );
}
